import { useState } from 'react';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import Logo from '../assets/logo.0d1eb692.svg';

interface SubMenuItem {
  name: string;
  href: string;
  external?: boolean;
}

interface NavigationItem {
  label: string;
  subItems: SubMenuItem[];
}

const navigationItems: NavigationItem[] = [
  {
    label: 'ABOUT US',
    subItems: [
      { name: 'Our Story', href: 'https://www.healthcity.org.uk/about-us', external: true },
      { name: 'Our Services', href: '/services' }
    ]
  },
  {
    label: 'TEAM',
    subItems: [
      { name: 'Clinical Team', href: '/teams?tab=clinical' },
      { name: 'Non-Clinical Team', href: '/teams?tab=non-clinical' }
    ]
  },
  {
    label: 'TREATMENTS',
    subItems: [
      { name: 'Issues We Solve', href: '/issues-we-solve' },
      { name: 'Approaches We Use', href: '/approaches-we-use' },
      { name: 'CBT', href: '/cbt' },
      { name: 'EAP', href: '/eap' },
      { name: 'Counselling', href: '/counseling' },
      { name: 'Couples Therapy', href: '/couples-therapy' },
      { name: 'Clinical Psychology', href: '/clinical-therapy' },
      { name: 'Coaching & Staff Wellbeing', href: '/coaching-staff-wellbeing' }
    ]
  },
  {
    label: 'SUPERVISION',
    subItems: [
      { name: 'Clinical Supervision', href: '/supervision' }
    ]
  },
  {
    label: 'MENTORING SERVICES',
    subItems: [
      { name: 'Youth Mentoring', href: '/youth-mentoring' },
      { name: 'Children Mentoring', href: '/children-mentoring' }
    ]
  },
  {
    label: 'FINANCIAL WELL-BEING',
    subItems: [
      { name: 'Financial Counseling', href: '/financial-wellbeing' }
    ]
  },
  {
    label: 'TWIN BRAIN DIAGNOSTICS',
    subItems: [
      { name: 'Psychiatry Diagnostics', href: '/psychiatry' },
      { name: 'Paid Assessments', href: '/assessments/paid' },
      { name: 'Free Assessments', href: '/assessments/free' }
    ]
  },
];

const secondaryNav: NavigationItem[] = [
  {
    label: 'RESOURCES',
    subItems: [
      { name: 'What is Mental Health Care in the UK?', href: '/what-is-mental-health-care' },
      { name: 'Keeping you Safe', href: '/keeping-you-safe' },
      { name: 'How to Pay', href: '/how-to-pay' },
      { name: 'Disclaimer', href: '/disclaimer' },
      { name: 'T&Cs', href: '/terms-and-conditions' },
      { name: 'References', href: '/references' }
    ]
  },
  {
    label: 'WEBINARS & TRAINING',
    subItems: [
      { name: 'Upcoming Events', href: '/webinars/upcoming' }
    ]
  },
  {
    label: 'BOOK NOW/PAYMENTS',
    subItems: [
      { name: 'Book Appointment', href: '/book/appointment' }
    ]
  },
  {
    label: 'CONTACT US',
    subItems: [
      { name: 'Contact Form', href: '/contact' },
      { name: 'Login', href: '/login' }
    ]
  },
];

export function DesktopNavigation() {
  return (
    <div className="hidden lg:block">
      {/* Top Navigation Bar */}
      <div className="bg-slate-700 text-white py-3 px-8">
        <div className="max-w-7xl mx-auto flex items-center">
          {/* Logo and Company Motto - Left Side with increased spacing */}
          <div className="flex items-center gap-3 mr-24">
            <Link to="/" className="flex items-center gap-3">
              <img src={Logo} alt="HealthCity Logo" className="h-12 w-auto" />
            </Link>
          </div>
          
          {/* Navigation Menu - Positioned with more space from logo */}
          <div className="flex-1 flex justify-start ml-12">
            <div className="flex items-center gap-6">
              {navigationItems.map((item, index) => {
                // For single-item menus, create direct links
                if (item.subItems.length === 1) {
                  const subItem = item.subItems[0];
                  return (
                    <div key={index} className="relative group pb-1">
                      {subItem.external ? (
                        <a 
                          href={subItem.href} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer"
                        >
                          <span className="text-xs whitespace-pre text-center font-bold">{item.label}</span>
                        </a>
                      ) : (
                        <Link 
                          to={subItem.href}
                          className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer"
                        >
                          <span className="text-xs whitespace-pre text-center font-bold">{item.label}</span>
                        </Link>
                      )}
                      {/* Hover underline */}
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 ease-in-out"></div>
                    </div>
                  );
                }
                
                // For multi-item menus, keep dropdowns
                return (
                  <DropdownMenu key={index}>
                    <DropdownMenuTrigger className="relative flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer group pb-1">
                      <span className="text-xs whitespace-pre text-center font-bold">{item.label}</span>
                      <ChevronDown className="h-3 w-3" />
                      {/* Hover underline */}
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 ease-in-out"></div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {item.subItems.map((subItem, subIndex) => (
                        <DropdownMenuItem key={subIndex} className="cursor-pointer">
                          {subItem.external ? (
                            <a href={subItem.href} target="_blank" rel="noopener noreferrer" className="w-full">
                              {subItem.name}
                            </a>
                          ) : (
                            <Link to={subItem.href} className="w-full">
                              {subItem.name}
                            </Link>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      
      {/* Secondary Navigation Bar */}
      <div className="bg-slate-600 text-white py-2 px-8">
        <div className="max-w-7xl mx-auto flex items-center">
          {/* Empty space to match logo section width exactly */}
          <div className="flex items-center gap-3 mr-24">
            <div className="h-12 w-auto"></div>
          </div>
          
          {/* Secondary Navigation - Centered between upper nav tabs */}
          <div className="flex-1 flex justify-center">
            <div className="flex items-center gap-16">
              {secondaryNav.map((item, index) => {
                // For single-item menus, create direct links
                if (item.subItems.length === 1) {
                  const subItem = item.subItems[0];
                  return (
                    <div key={index} className="relative group pb-1">
                      {subItem.external ? (
                        <a 
                          href={subItem.href} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer"
                        >
                          <span className="text-xs whitespace-pre text-center font-bold">{item.label}</span>
                        </a>
                      ) : (
                        <Link 
                          to={subItem.href}
                          className="flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer"
                        >
                          <span className="text-xs whitespace-pre text-center font-bold">{item.label}</span>
                        </Link>
                      )}
                      {/* Hover underline */}
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 ease-in-out"></div>
                    </div>
                  );
                }
                
                // For multi-item menus, keep dropdowns
                return (
                  <DropdownMenu key={index}>
                    <DropdownMenuTrigger className="relative flex items-center gap-1 hover:opacity-80 transition-opacity cursor-pointer group pb-1">
                      <span className="text-xs whitespace-pre text-center font-bold">{item.label}</span>
                      <ChevronDown className="h-3 w-3" />
                      {/* Hover underline */}
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 ease-in-out"></div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {item.subItems.map((subItem, subIndex) => (
                        <DropdownMenuItem key={subIndex} className="cursor-pointer">
                          {subItem.external ? (
                            <a href={subItem.href} target="_blank" rel="noopener noreferrer" className="w-full">
                              {subItem.name}
                            </a>
                          ) : (
                            <Link to={subItem.href} className="w-full">
                              {subItem.name}
                            </Link>
                          )}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function MobileNavigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  const allMenuItems = [...navigationItems, ...secondaryNav];

  return (
    <>
      <div className="lg:hidden bg-slate-700 py-4 px-4">
        <div className="flex items-center justify-between">
          {/* Logo and Company Motto - Far Left */}
          <Link to="/" className="flex items-center gap-2">
            <img src={Logo} alt="HealthCity Logo" className="h-8 w-auto" />
          </Link>
          
          {/* Menu Button */}
          <button 
            onClick={() => setIsOpen(!isOpen)}
            className="text-white p-2 z-50 relative"
          >
            {isOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50" onClick={() => setIsOpen(false)} />
      )}

      {/* Mobile Menu Content */}
      <div className={`lg:hidden fixed top-0 left-0 h-full w-80 bg-slate-700 text-white z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold">Menu</h2>
            <button 
              onClick={() => setIsOpen(false)}
              className="text-white"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <div className="space-y-4">
            {allMenuItems.map((item, index) => {
              // For single-item menus, create direct links
              if (item.subItems.length === 1) {
                const subItem = item.subItems[0];
                return (
                  <div key={index} className="border-b border-slate-600 pb-3">
                    {subItem.external ? (
                      <a
                        href={subItem.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          setIsOpen(false);
                          setOpenSubmenu(null);
                        }}
                        className="w-full flex items-center text-left py-2 hover:text-gray-300 transition-colors"
                      >
                        <span className="text-sm font-bold">{item.label}</span>
                      </a>
                    ) : (
                      <Link
                        to={subItem.href}
                        onClick={() => {
                          setIsOpen(false);
                          setOpenSubmenu(null);
                        }}
                        className="w-full flex items-center text-left py-2 hover:text-gray-300 transition-colors"
                      >
                        <span className="text-sm font-bold">{item.label}</span>
                      </Link>
                    )}
                  </div>
                );
              }
              
              // For multi-item menus, keep dropdowns
              return (
                <div key={index} className="border-b border-slate-600 pb-3">
                  <button
                    onClick={() => setOpenSubmenu(openSubmenu === item.label ? null : item.label)}
                    className="w-full flex items-center justify-between text-left py-2 hover:text-gray-300 transition-colors"
                  >
                    <span className="text-sm font-bold">{item.label}</span>
                    <ChevronDown 
                      className={`h-4 w-4 transition-transform ${
                        openSubmenu === item.label ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openSubmenu === item.label && (
                    <div className="mt-2 ml-4 space-y-2">
                      {item.subItems.map((subItem, subIndex) => (
                        subItem.external ? (
                          <a
                            key={subIndex}
                            href={subItem.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              setIsOpen(false);
                              setOpenSubmenu(null);
                            }}
                            className="block w-full text-left py-1 text-sm text-gray-300 hover:text-white transition-colors"
                          >
                            {subItem.name}
                          </a>
                        ) : (
                          <Link
                            key={subIndex}
                            to={subItem.href}
                            onClick={() => {
                              setIsOpen(false);
                              setOpenSubmenu(null);
                            }}
                            className="block w-full text-left py-1 text-sm text-gray-300 hover:text-white transition-colors"
                          >
                            {subItem.name}
                          </Link>
                        )
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
